/**
 * Combinaisons du 421 — portage TypeScript de la règle de référence.
 *
 * La source de vérité historique est `evalCombo()` de la v1 (`app.js`), déjà
 * prouvée identique au simulateur Python (`sim/check_parity.py`, 216 tirages,
 * zéro divergence). Ce portage est verrouillé par le même procédé :
 * `tests/combinaisons.test.ts` compare les 216 tirages à un fichier de
 * référence produit par le Python.
 *
 * Hiérarchie : 421 > 111 > 611 > 666 > 511 > 555 > … > 211 > 222 > suites >
 * lecture > nénette (221).
 */
/**
 * Évalue une main. Les dés sont interchangeables : l'ordre n'a aucune
 * importance, seule la combinaison compte.
 */
export function evaluer(a, b, c) {
    // Tri décroissant sans passer par un tableau indexé : la médiane est la somme
    // moins les extrêmes, ce qui reste juste en présence de doublons.
    const x = Math.max(a, b, c);
    const z = Math.min(a, b, c);
    const y = (a + b + c - x - z);
    const cle = `${x}${y}${z}`;
    if (cle === "421") {
        return { score: 1000, jetons: 10, cle, famille: "421", hauteur: x };
    }
    if (x === y && y === z) {
        if (x === 1)
            return { score: 900, jetons: 7, cle, famille: "brelanAs", hauteur: x };
        return { score: 800 + x * 10, jetons: x, cle, famille: "brelan", hauteur: x };
    }
    // Les paires d'as s'intercalent entre les brelans : 611 > 666 > 511 > 555…
    if (y === 1 && z === 1) {
        return { score: 805 + x * 10, jetons: x, cle, famille: "paireAs", hauteur: x };
    }
    if (cle === "221") {
        return { score: 100, jetons: 2, cle, famille: "nenette", hauteur: x };
    }
    if (x === y + 1 && y === z + 1) {
        return { score: 700 + x, jetons: 2, cle, famille: "suite", hauteur: x };
    }
    // « Lecture » : on lit les trois dés du plus grand au plus petit.
    return { score: x * 100 + y * 10 + z, jetons: 1, cle, famille: "lecture", hauteur: x };
}
/** Les 56 mains distinctes, de la plus FORTE à la plus FAIBLE. */
export const MAINS = (() => {
    const out = [];
    for (let x = 1; x <= 6; x++) {
        for (let y = x; y <= 6; y++) {
            for (let z = y; z <= 6; z++) {
                out.push([z, y, x]);
            }
        }
    }
    return out.sort((m, n) => evaluerMain(n).score - evaluerMain(m).score);
})();
/** Évalue une main déjà constituée. */
export function evaluerMain(main) {
    return evaluer(main[0], main[1], main[2]);
}
/** Clé canonique d'une main, indépendante de l'ordre des dés. */
export function cle(main) {
    return evaluerMain(main).cle;
}
/** Compare deux mains : > 0 si `a` est plus forte. */
export function comparer(a, b) {
    return evaluerMain(a).score - evaluerMain(b).score;
}
/**
 * Probabilité théorique de chaque main sur un lancer de trois dés neufs.
 * Une main à trois valeurs distinctes sort 6 fois sur 216, une paire 3 fois, un
 * brelan 1 fois. Sert de référence au test d'équité du tirage.
 */
export const PROBA_MAIN = (() => {
    const out = {};
    for (const m of MAINS) {
        const distinctes = new Set(m).size;
        const permutations = distinctes === 3 ? 6 : distinctes === 2 ? 3 : 1;
        out[evaluerMain(m).cle] = permutations / 216;
    }
    return out;
})();
//# sourceMappingURL=combinaisons.js.map